import * as React from 'react';

import { get as getUser } from 'js/lib/user';

import { useTracker } from '@coursera/event-pulse/react';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { isCoachTutorGAEpic, useProductFeatures } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';
import { useIsCourseEligibleForCoachAccess } from 'bundles/enroll/utils/freemiumModelUtils';
import { IdTypes } from 'bundles/product-features';

type Props = {
  children: (props: { showEmbeddedCoach: boolean }) => JSX.Element | null;
  courseId?: string;
  epicImpression?: boolean;
};

function useCoachPermissions({
  courseId = '',
  epicImpression = false,
}: {
  courseId?: string;
  epicImpression?: boolean;
}) {
  const userId = getUser().id?.toString();
  const [showEmbeddedCoach, setShowEmbeddedCoach] = React.useState<boolean>(false);

  // useIsCourseEligibleForCoachAccess will check if user is in the freemium enabled variant, if they have not completed module 1 for the current course, and if they are eligible to see Coach
  const { isCourseEligibleForCoachAccess, loading: freemiumCheckLoading } = useIsCourseEligibleForCoachAccess(courseId);

  const { loading, error, features } = useProductFeatures({ idType: IdTypes.User, id: userId });
  const isCoachEnterpriseEnabled = features?.get?.('enterprise', 'enableAiCoach');
  const isCoachDegreesEnabled = features?.get?.('degrees', 'enableAiCoachForDegrees');
  const isCoachTutorGA = isCoachTutorGAEpic({ epicImpression });

  const track = useTracker();

  React.useEffect(() => {
    if (freemiumCheckLoading || loading || error || !features) {
      if (error) {
        captureMessageInSentry({
          level: 'warning',
          error,
          source: 'useCoachPermissions',
          extra: {
            errorString: JSON.stringify(error, null, 2),
            externalId: getUser().external_id,
            isCoachEnterpriseEnabled,
            isCoachDegreesEnabled,
            isCoachTutorGA,
          },
        });
      }
      setShowEmbeddedCoach(false);
      return;
    }

    // Check if learner is a freemium user with Coach access by calling useIsCourseEligibleForCoachAccess, if TRUE show Coach, if FALSE continue with existing Coach access check with product features and GA epic
    if (courseId && isCourseEligibleForCoachAccess) {
      setShowEmbeddedCoach(true);
      track('access_coach_freemium', {
        product: {
          type: 'course',
          id: courseId,
        },
      });
    } else {
      // always enabled for Enterprise, if it is not enterprise enabled then check the existing access group or holdout GA epic
      setShowEmbeddedCoach(isCoachDegreesEnabled || isCoachEnterpriseEnabled || isCoachTutorGA);
    }
  }, [
    freemiumCheckLoading,
    courseId,
    loading,
    error,
    features,
    isCoachEnterpriseEnabled,
    isCoachDegreesEnabled,
    isCoachTutorGA,
    isCourseEligibleForCoachAccess,
    track,
  ]);

  return { showEmbeddedCoach };
}

const CoachPermissionsWrapper = ({ children, courseId, epicImpression }: Props) => {
  const { showEmbeddedCoach } = useCoachPermissions({ courseId, epicImpression });

  return children({ showEmbeddedCoach });
};

export { CoachPermissionsWrapper, useCoachPermissions };
